import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { SEO } from '../../utils/seo';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: '橘穂の算命学カウンセリング',
    description: '算命学による運命鑑定・人生相談。女性限定・完全予約制。オンライン鑑定（Google Meet）と対面鑑定をご提供。',
    url: siteUrl,
    telephone: '',
    priceRange: '¥4,000-¥11,000',
    address: {
      '@type': 'PostalAddress',
      addressRegion: '東京都',
      addressLocality: '板橋区',
      addressCountry: 'JP',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '35.7514',
      longitude: '139.6550',
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Monday',
        opens: '16:00',
        closes: '18:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Monday',
        opens: '21:00',
        closes: '23:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Wednesday',
        opens: '16:00',
        closes: '18:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Wednesday',
        opens: '21:00',
        closes: '23:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Thursday',
        opens: '16:00',
        closes: '18:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Thursday',
        opens: '21:00',
        closes: '23:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Friday',
        opens: '16:00',
        closes: '18:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Friday',
        opens: '21:00',
        closes: '23:00',
      },
    ],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '5',
      reviewCount: '4',
    },
  };

  // Handle scroll‑position changes for the sticky navigation bar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to hash fragment after navigation (e.g. /#contact)
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      // Give the browser a moment to render the target element
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  // Helper to scroll to a section and close the mobile menu
  const scrollToSection = (id: string) => {
    setIsMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F1E8] to-white overflow-x-hidden">
      <SEO
        title="橘穂の算命学カウンセリング｜女性限定・完全予約制｜東武東上線沿線"
        description="算命学による運命鑑定・人生相談。女性限定・完全予約制。オンライン鑑定（Google Meet）と対面鑑定をご提供。自然思想に基づく学問的アプローチで、人生の流れを読み解きます。東武東上線沿線（東武練馬駅・上板橋駅・ときわ台駅周辺）"
        keywords="算命学,鑑定,女性限定,オンライン鑑定,対面鑑定,東武東上線,運命鑑定,人生相談,橘穂"
        canonical={siteUrl}
        ogTitle="橘穂の算命学カウンセリング｜女性限定・完全予約制"
        ogDescription="算命学による運命鑑定・人生相談。女性限定・完全予約制。自然思想に基づく学問的アプローチで、人生の流れを読み解きます。"
        schema={schema}
      />

      {/* Navigation */}
      <nav
        className={`fixed w-full z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              <img
                src="https://public.readdy.ai/ai/img_res/dab67413-5a70-4602-b752-4997924ca2b6.png"
                alt="橘穂の算命学カウンセリング ロゴ"
                className="w-9 h-9 sm:w-12 sm:h-12 flex-shrink-0"
              />
              <span className="text-sm sm:text-lg font-serif text-[#8B4513] whitespace-nowrap truncate">
                橘穂の算命学カウンセリング
              </span>
            </div>

            {/* Desktop navigation */}
            <div className="hidden lg:flex items-center gap-10">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className={`text-sm font-light tracking-wide transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'text-[#2C2C2C] hover:text-[#6B8E7F]'
                    : 'text-white hover:text-white/80'
                }`}
              >
                ホーム
              </button>
              <Link
                to="/about"
                className={`text-sm font-light tracking-wide transition-colors whitespace-nowrap ${
                  isScrolled
                    ? 'text-[#2C2C2C] hover:text-[#6B8E7F]'
                    : 'text-white hover:text-white/80'
                }`}
              >
                算命学とは
              </Link>
              <Link
                to="/profile"
                className={`text-sm font-light tracking-wide transition-colors whitespace-nowrap ${
                  isScrolled
                    ? 'text-[#2C2C2C] hover:text-[#6B8E7F]'
                    : 'text-white hover:text-white/80'
                }`}
              >
                プロフィール
              </Link>
              <button
                onClick={() => scrollToSection('menu')}
                className={`text-sm font-light tracking-wide transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'text-[#2C2C2C] hover:text-[#6B8E7F]'
                    : 'text-white hover:text-white/80'
                }`}
              >
                メニュー
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className={`text-sm font-light tracking-wide transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'text-[#2C2C2C] hover:text-[#6B8E7F]'
                    : 'text-white hover:text-white/80'
                }`}
              >
                ご予約
              </button>
            </div>

            {/* CTA button (desktop) */}
            <div className="hidden lg:block">
              <button
                onClick={() => scrollToSection('campaign')}
                className={`px-5 py-2 rounded-full border transition-all whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'border-[#2C2C2C] text-[#2C2C2C] hover:bg-[#2C2C2C] hover:text-white'
                    : 'border-white text-white hover:bg-white hover:text-[#2C2C2C]'
                }`}
              >
                <span className="text-sm mr-1">初回</span>
                <span className="text-sm">特別鑑定</span>
              </button>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
                isScrolled ? 'text-[#2C2C2C]' : 'text-white'
              }`}
            >
              <i
                className={`${
                  isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
                } text-2xl`}
              ></i>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white shadow-lg border-t border-[#E0E0E0]">
            <div className="px-4 py-4 space-y-1">
              <button
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-3 text-sm font-light text-[#2C2C2C] hover:bg-[#F5F1E8] rounded-lg cursor-pointer"
              >
                ホーム
              </button>
              <Link
                to="/about"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-light text-[#2C2C2C] hover:bg-[#F5F1E8] rounded-lg"
              >
                算命学とは
              </Link>
              <Link
                to="/profile"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-light text-[#2C2C2C] hover:bg-[#F5F1E8] rounded-lg"
              >
                プロフィール
              </Link>
              <button
                onClick={() => scrollToSection('menu')}
                className="block w-full text-left px-4 py-3 text-sm font-light text-[#2C2C2C] hover:bg-[#F5F1E8] rounded-lg cursor-pointer"
              >
                メニュー
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="block w-full text-left px-4 py-3 text-sm font-light text-[#2C2C2C] hover:bg-[#F5F1E8] rounded-lg cursor-pointer"
              >
                ご予約
              </button>
              <div className="pt-2 px-4">
                <button
                  onClick={() => scrollToSection('campaign')}
                  className="w-full py-3 rounded-full border border-[#2C2C2C] text-[#2C2C2C] text-sm font-light hover:bg-[#2C2C2C] hover:text-white transition-all whitespace-nowrap cursor-pointer"
                >
                  初回特別鑑定
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* --------------------------------------------------------------------- */}
      {/* Hero Section */}
      {/* --------------------------------------------------------------------- */}
      <section className="relative h-screen flex items-center justify-start overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/42f513ccc4e2e48b3b4525c502da66d0.jpg"
            alt="Japanese landscape background"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full">
          <div className="max-w-xl">
            <div className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-white/60 backdrop-blur-sm mb-6 sm:mb-8">
              <span className="text-white text-[10px] sm:text-xs font-light tracking-widest whitespace-nowrap">
                女性限定・完全予約制
              </span>
            </div>
            <h1
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extralight text-white leading-relaxed tracking-wide mb-4 sm:mb-6"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              あなたの人生に、<br />
              新しい視点を。
            </h1>
            <p className="text-white/90 text-sm sm:text-base font-light leading-relaxed tracking-wide mb-8 sm:mb-10">
              自然の摂理に基づく学問的アプローチで、<br />
              人生の流れを読み解きます。
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => scrollToSection('contact')}
                className="px-8 sm:px-10 py-3.5 sm:py-4 bg-[#6B8E7F] text-white rounded-full text-sm sm:text-base font-light tracking-wider hover:bg-[#5A7D6E] transition-colors whitespace-nowrap text-center cursor-pointer w-full sm:w-auto"
              >
                鑑定を申し込む
              </button>
              <button
                onClick={() => scrollToSection('menu')}
                className="px-8 sm:px-10 py-3.5 sm:py-4 bg-white/20 backdrop-blur-sm text-white border border-white/40 rounded-full text-sm font-light tracking-wider hover:bg-white/30 transition-colors whitespace-nowrap text-center cursor-pointer w-full sm:w-auto"
              >
                鑑定メニューを見る
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------------------- */}
      {/* About Section */}
      {/* --------------------------------------------------------------------- */}
      <section id="about" className="py-16 sm:py-24 lg:py-32 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-8 sm:mb-12">
            <span className="text-[#8B7355] text-sm font-light tracking-widest">
              / About Sanmeigaku
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20minimalist%20representation%20of%20five%20elements%20wood%20fire%20earth%20metal%20water%20in%20Japanese%20aesthetic%20style%20soft%20muted%20earth%20tones%20beige%20cream%20sage%20green%20subtle%20geometric%20shapes%20flowing%20organic%20forms%20representing%20natural%20philosophy%20ancient%20wisdom%20modern%20interpretation%20clean%20elegant%20composition%20harmonious%20balance&width=800&height=800&seq=about-img-001&orientation=squarish"
                alt="算命学"
                className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover object-top rounded-lg"
              />
            </div>
            <div>
              <h2
                className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#2C2C2C] mb-6 sm:mb-8 leading-relaxed"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                算命学とは
              </h2>
              <p className="text-[#4A4A4A] text-sm sm:text-base font-light leading-loose mb-4 sm:mb-6">
                算命学は、古代中国に起源を持ち、人間もこの地球上に生を受けた自然の産物であり、自然の一部分であるという自然思想に基づいた学問です。
              </p>
              <p className="text-[#4A4A4A] text-sm sm:text-base font-light leading-loose mb-4 sm:mb-6">
                生年月日から導き出される命式をもとに、その人が持って生まれた才能や思考の傾向、行動パターン、人生の流れを読み解いていきます。
              </p>
              <p className="text-[#4A4A4A] text-sm sm:text-base font-light leading-loose mb-8 sm:mb-10">
                長い人生航路を安全に進む為の『航路図』とも言えるでしょう。
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-[#2C2C2C] text-white rounded-full text-sm font-light tracking-wider hover:bg-[#3C3C3C] transition-all whitespace-nowrap"
              >
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line text-lg"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------------------- */}
      {/* Profile Section */}
      {/* --------------------------------------------------------------------- */}
      <section id="profile" className="py-16 sm:py-24 lg:py-32 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-8 sm:mb-12">
            <span className="text-[#8B7355] text-sm font-light tracking-widest">
              / Profile
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center">
            <div className="order-2 lg:order-1">
              <h2
                className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#2C2C2C] mb-4 leading-relaxed"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                鑑定士紹介
              </h2>
              <p className="text-[#8B7355] text-xs sm:text-sm font-light mb-6 sm:mb-8">
                算命学十三代宗家系譜 算命学占星術認定鑑定士
              </p>
              <h3
                className="text-xl sm:text-2xl font-light text-[#2C2C2C] mb-6 sm:mb-8"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                橘穂（きつほ）
              </h3>
              <p className="text-[#4A4A4A] text-sm sm:text-base font-light leading-loose mb-4 sm:mb-6">
                正看護師として病院勤務をしていた中で、人生の波の違いに疑問を抱き、その答えを紐解いてくれる学問として算命学と出会いました。
              </p>
              <p className="text-[#4A4A4A] text-sm sm:text-base font-light leading-loose mb-4 sm:mb-6">
                現在も師範の元に通い、日々学びを深めながら、お客様一人ひとりの人生のライフステージを丁寧に読み解いております。
              </p>
              <p className="text-[#4A4A4A] text-sm sm:text-base font-light leading-loose mb-8 sm:mb-10">
                最初は何が悩みなのか言葉にできなくても問題ありません。小さな事でもお気軽にご連絡ください。
              </p>
              <Link
                to="/profile"
                className="inline-flex items-center gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-[#2C2C2C] text-white rounded-full text-sm font-light tracking-wider hover:bg-[#3C3C3C] transition-all whitespace-nowrap"
              >
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line text-lg"></i>
              </Link>
            </div>
            <div className="order-1 lg:order-2 flex items-center justify-center">
              <img
                src="https://static.readdy.ai/image/b84ab4ce2ae3714b9b5e2bb5be24c7fc/5c742ae525d0e33c93b3ced6ac937702.jpeg"
                alt="橘穂プロフィール"
                className="w-[85%] h-[240px] sm:h-[320px] lg:h-[400px] object-contain object-center rounded-lg mx-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------------------- */}
      {/* Menu Section */}
      {/* --------------------------------------------------------------------- */}
      <section id="menu" className="py-16 sm:py-24 lg:py-32 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-10 sm:mb-16">
            <span className="text-[#999999] text-xs font-light tracking-widest mb-2 block">
              Divination Menu
            </span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2C2C2C]"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              鑑定メニュー
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {/* First Time Special */}
            <div className="bg-[#F8F6F1] rounded-2xl p-6 sm:p-8 hover:shadow-lg transition-shadow border border-[#E8E4DC]">
              <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/60 mb-4 sm:mb-6">
                <span className="text-[#6B8E7F] text-xs font-light tracking-widest">
                  初回限定
                </span>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-[#2C2C2C] mb-3 sm:mb-4">
                初回鑑定
              </h3>
              <p className="text-[#6B8E7F] text-xs font-light mb-4 sm:mb-6">
                オンライン限定（Google Meet）
              </p>
              <div className="space-y-2 mb-4 sm:mb-6">
                <p className="text-[#666666] text-sm font-light">30分　4,000円</p>
              </div>
              <p className="text-[#4A4A4A] text-xs font-light leading-relaxed">
                算命学が初めての方向けのオンライン鑑定。主に持って生まれた宿命、使命、才能を中心にお伝えいたします。
              </p>
            </div>

            {/* Online */}
            <div className="bg-[#F8F6F1] rounded-2xl p-6 sm:p-8 hover:shadow-lg transition-shadow">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-computer-line text-2xl sm:text-3xl text-[#2C2C2C]"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-[#2C2C2C] mb-3 sm:mb-4">
                オンライン鑑定（Google Meet）
              </h3>
              <div className="space-y-3 mb-4 sm:mb-6">
                <p className="text-[#666666] text-sm font-light">30分　5,000円</p>
                <div className="bg-white/60 rounded-lg px-3 py-2 border border-[#6B8E7F]/20">
                  <p className="text-[#6B8E7F] text-sm font-light">60分　10,000円</p>
                  <p className="text-[#8B7355] text-xs font-light mt-1">
                    じっくりご相談いただけます
                  </p>
                </div>
                <p className="text-[#999999] text-xs font-light mt-3">
                  延長10分　1,500円
                </p>
              </div>
              <p className="text-[#8B7355] text-xs font-light">
                Google Meet を使用したオンライン鑑定です。ご予約完了メールに記載されている参加リンクを押すだけでご参加いただけます。
              </p>
            </div>

            {/* In‑person */}
            <div className="bg-[#F8F6F1] rounded-2xl p-6 sm:p-8 hover:shadow-lg transition-shadow">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-cup-line text-2xl sm:text-3xl text-[#2C2C2C]"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-[#2C2C2C] mb-3 sm:mb-4">
                対面鑑定
              </h3>
              <div className="space-y-2 mb-4 sm:mb-6">
                <p className="text-[#666666] text-sm font-light">30分　6,000円</p>
                <p className="text-[#666666] text-sm font-light">60分　11,000円</p>
                <p className="text-[#999999] text-xs font-light mt-3">
                  延長10分　1,500円
                </p>
              </div>
              <p className="text-[#8B7355] text-xs font-light">
                東武東上線沿線カフェにて（16:00から18:00まで）
              </p>
              <p className="text-[#999999] text-xs font-light mt-1">
                （東武練馬駅・上板橋駅・ときわ台駅周辺）
              </p>
            </div>
          </div>

          <div className="text-center mt-10 sm:mt-14">
            <button
              onClick={() => scrollToSection('contact')}
              className="inline-block px-10 sm:px-14 py-3.5 sm:py-4 bg-[#6B8E7F] text-white rounded-full text-sm sm:text-base font-light tracking-wider hover:bg-[#5A7D6E] transition-colors whitespace-nowrap cursor-pointer w-full sm:w-auto text-center"
            >
              鑑定を申し込む
            </button>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------------------- */}
      {/* Schedule Section */}
      {/* --------------------------------------------------------------------- */}
      <section className="py-16 sm:py-24 lg:py-32 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-10 sm:mb-16">
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-extralight text-[#2C2C2C] mb-4"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              ご予約について
            </h2>
            <p className="text-[#8B7355] text-sm font-light">完全予約制・女性限定</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {/* Schedule */}
            <div className="bg-white rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
              <div className="bg-[#E8EEF1] h-32 sm:h-48 flex items-center justify-center">
                <i className="ri-calendar-line text-4xl sm:text-6xl text-[#6B8E7F]"></i>
              </div>
              <div className="p-5 sm:p-8">
                <h3 className="text-lg font-semibold text-[#2C2C2C] mb-4 sm:mb-6 text-center">
                  営業時間
                </h3>
                <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6 text-xs sm:text-sm font-light text-[#4A4A4A]">
                  <div className="flex justify-between gap-2">
                    <span className="flex-shrink-0">月曜日</span>
                    <span className="text-right text-[10px] sm:text-sm">
                      16:00-18:00 / 21:00-23:00
                    </span>
                  </div>
                  <div className="flex justify-between gap-2 text-[#999999]">
                    <span className="flex-shrink-0">火曜日</span>
                    <span className="text-right">定休日</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="flex-shrink-0">水曜日</span>
                    <span className="text-right text-[10px] sm:text-sm">
                      16:00-18:00 / 21:00-23:00
                    </span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="flex-shrink-0">木曜日</span>
                    <span className="text-right text-[10px] sm:text-sm">
                      16:00-18:00 / 21:00-23:00
                    </span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="flex-shrink-0">金曜日</span>
                    <span className="text-right text-[10px] sm:text-sm">
                      16:00-18:00 / 21:00-23:00
                    </span>
                  </div>
                  <div className="flex justify-between gap-2 text-[#999999]">
                    <span className="flex-shrink-0">土・日・祝</span>
                    <span className="text-right">定休日</span>
                  </div>
                </div>
                <p className="text-[10px] sm:text-xs text-center">
                  対面鑑定は16:00-18:00のみ
                </p>
              </div>
            </div>

            {/* Required Info */}
            <div className="bg-white rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
              <div className="bg-[#F0EDE8] h-32 sm:h-48 flex items-center justify-center">
                <i className="ri-file-list-3-line text-4xl sm:text-6xl text-[#8B7355]"></i>
              </div>
              <div className="p-5 sm:p-8">
                <h3 className="text-lg font-semibold text-[#2C2C2C] mb-4 sm:mb-6 text-center">
                  ご予約時の情報
                </h3>
                <p className="text-xs sm:text-sm font-light text-[#4A4A4A] mb-4">
                  ご予約の際は、以下の情報をご記入ください。
                </p>
                <ul className="space-y-3 mb-4 sm:mb-6 text-xs sm:text-sm font-light text-[#4A4A4A]">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#8B7355] mt-1.5 flex-shrink-0"></span>
                    <span>お名前</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#8B7355] mt-1.5 flex-shrink-0"></span>
                    <span>ご希望日時</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#8B7355] mt-1.5 flex-shrink-0"></span>
                    <span>携帯番号</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#8B7355] mt-1.5 flex-shrink-0"></span>
                    <span>ご本人の生年月日・性別</span>
                  </li>
                </ul>
                <p className="text-xs text-[#999999] mb-2">相性鑑定の場合：</p>
                <p className="text-xs text-[#4A4A4A] mb-3">お相手の生年月日・性別</p>
                <p className="text-xs text-[#999999] mb-2">親子鑑定の場合：</p>
                <p className="text-xs text-[#4A4A4A]">ご同居家族全員の生年月日・性別</p>
              </div>
            </div>

            {/* Conditions */}
            <div className="bg-white rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
              <div className="bg-[#E8F0ED] h-32 sm:h-48 flex items-center justify-center">
                <i className="ri-user-heart-line text-4xl sm:text-6xl text-[#6B8E7F]"></i>
              </div>
              <div className="p-5 sm:p-8">
                <h3 className="text-lg font-semibold text-[#2C2C2C] mb-4 sm:mb-6 text-center">
                  ご利用条件
                </h3>
                <div className="space-y-4 mb-6 sm:mb-8">
                  <div className="text-center">
                    <p className="text-base font-light text-[#4A4A4A]">女性限定</p>
                    <p className="text-base font-light text-[#4A4A4A]">
                      完全予約制
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="w-full py-3 bg-[#2C2C2C] text-white rounded-full text-sm font-light tracking-wider hover:bg-[#3C3C3C] transition-colors whitespace-nowrap"
                >
                  鑑定を申し込む
                </button>
                <p className="text-xs text-[#999999] text-center mt-4">
                  Google Meetまたは対面
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------------------- */}
      {/* Reviews Section */}
      {/* --------------------------------------------------------------------- */}
      <section className="py-16 sm:py-24 lg:py-32 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-10 sm:mb-16 text-center">
            <span className="text-[#999999] text-xs font-light tracking-widest mb-2 block">
              Customer Voice
            </span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2C2C2C]"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              お客様のご感想
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto mb-10 sm:mb-12">
            {/* Review 1 */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#D4A574] text-sm"></i>
                ))}
              </div>
              <p className="text-[#4A4A4A] text-sm font-light leading-relaxed">
                とても丁寧で安心して相談できました
              </p>
            </div>

            {/* Review 2 */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#D4A574] text-sm"></i>
                ))}
              </div>
              <p className="text-[#4A4A4A] text-sm font-light leading-relaxed">
                自分の状況が整理され、前向きな気持ちになれました
              </p>
            </div>

            {/* Review 3 */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#D4A574] text-sm"></i>
                ))}
              </div>
              <p className="text-[#4A4A4A] text-sm font-light leading-relaxed">
                落ち着いた雰囲気で話しやすく、的確なアドバイスをいただけました
              </p>
            </div>

            {/* Review 4 */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#D4A574] text-sm"></i>
                ))}
              </div>
              <p className="text-[#4A4A4A] text-sm font-light leading-relaxed">
                人生の流れを知ることで、今後の方向性が見えてきました
              </p>
            </div>
          </div>

          <div className="text-center">
            <a
              href="https://www.google.com/search?q=%E6%A9%98%E7%A9%82%E3%81%AE%E7%AE%97%E5%91%BD%E5%AD%A6%E3%82%AB%E3%82%A6%E3%83%B3%E3%82%BB%E3%83%AA%E3%83%B3%E3%82%B0"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="text-[#6B8E7F] text-sm font-light hover:text-[#5A7D6E] transition-colors inline-flex items-center gap-2"
            >
              <span>Googleのレビューを見る</span>
              <i className="ri-external-link-line text-base"></i>
            </a>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------------------- */}
      {/* Campaign Section */}
      {/* --------------------------------------------------------------------- */}
      <section id="campaign" className="py-16 sm:py-24 lg:py-32 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-10 sm:mb-16 text-center">
            <span className="text-[#999999] text-xs font-light tracking-widest mb-2 block">
              First Session
            </span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2C2C2C]"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              初回のご案内
            </h2>
          </div>

          <div className="max-w-xl mx-auto">
            {/* First Time Special */}
            <div className="bg-gradient-to-br from-[#E8F0ED] to-[#F5F1E8] rounded-2xl p-6 sm:p-10 hover:shadow-lg transition-shadow">
              <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/60 mb-4 sm:mb-6">
                <span className="text-[#6B8E7F] text-xs font-light tracking-widest">
                  初回限定
                </span>
              </div>
              <h3
                className="text-2xl sm:text-3xl font-light text-[#2C2C2C] mb-4 sm:mb-6 leading-relaxed"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                初回特別セッション
              </h3>
              <p className="text-[#4A4A4A] text-sm sm:text-base font-light leading-loose mb-6 sm:mb-8">
                初めてご利用いただく方へ、オンライン限定の特別セッションをご用意しております。算命学との出会いが、あなたにとって意味のある時間となりますように。
              </p>
              <div className="bg-white/50 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
                <p className="text-[#2C2C2C] text-base sm:text-lg font-light mb-2">
                  初回特別価格
                </p>
                <p className="text-[#6B8E7F] text-2xl sm:text-3xl font-light">
                  4,000円
                </p>
                <p className="text-[#999999] text-xs font-light mt-2">
                  30分セッション（Google Meet）
                </p>
              </div>
              <p className="text-[#8B7355] text-sm font-light text-center">
                人生の節目に、新しい視点を
              </p>
            </div>
          </div>

          <div className="text-center mt-8 sm:mt-12">
            <button
              onClick={() => scrollToSection('contact')}
              className="inline-block px-10 sm:px-14 py-3.5 sm:py-4 bg-[#6B8E7F] text-white rounded-full text-sm sm:text-base font-light tracking-wider hover:bg-[#5A7D6E] transition-colors whitespace-nowrap cursor-pointer w-full sm:w-auto text-center"
            >
              鑑定を申し込む
            </button>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------------------- */}
      {/* Contact Section */}
      {/* --------------------------------------------------------------------- */}
      <section id="contact" className="py-16 sm:py-24 lg:py-32 bg-[#FDFBF7]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#2C2C2C] mb-4"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              鑑定のお申し込み
            </h2>
            <p className="text-[#8B7355] text-sm font-light">
              フォームよりお気軽にご連絡ください
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F0EDE8]">
                <i className="ri-mail-send-line text-base text-[#8B7355]"></i>
              </div>
              <h3 className="text-base font-light text-[#2C2C2C]" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                お申し込みフォーム
              </h3>
            </div>
            <form
              id="contact-form"
              data-readdy-form
              action="https://readdy.ai/api/form/d6g1ra1ghdq4qda7002g"
              method="POST"
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const formData = new FormData(form);

                const name = formData.get('name');
                const email = formData.get('email');
                const phone = formData.get('phone');
                const menu = formData.get('menu');
                const content = formData.get('content');
                const datetime = formData.get('datetime');
                const divinationInfo = formData.get('divination-info');

                const statusEl = document.getElementById('form-status');

                if (
                  !name || (typeof name === 'string' && name.trim() === '') ||
                  !email || (typeof email === 'string' && email.trim() === '') ||
                  !phone || (typeof phone === 'string' && phone.trim() === '') ||
                  !menu || (typeof menu === 'string' && menu.trim() === '') ||
                  !content || (typeof content === 'string' && content.trim() === '') ||
                  !datetime || (typeof datetime === 'string' && datetime.trim() === '')
                ) {
                  if (statusEl) {
                    statusEl.textContent = '必須項目をすべてご入力ください。';
                    statusEl.className = 'text-center text-sm text-red-500 mt-4';
                  }
                  return;
                }

                const params = new URLSearchParams();
                params.append('name', (name as string).trim());
                params.append('email', (email as string).trim());
                params.append('phone', (phone as string).trim());
                params.append('menu', (menu as string).trim());
                params.append('content', (content as string).trim());
                params.append('datetime', (datetime as string).trim());
                if (divinationInfo && typeof divinationInfo === 'string' && divinationInfo.trim() !== '') {
                  params.append('divination-info', divinationInfo.trim());
                }

                fetch('https://readdy.ai/api/form/d6g1ra1ghdq4qda7002g', {
                  method: 'POST',
                  body: params.toString(),
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                  },
                })
                  .then((response) => {
                    if (statusEl) {
                      if (response.ok) {
                        statusEl.textContent = '送信ありがとうございます。内容を確認後、こちらからご連絡いたします。';
                        statusEl.className = 'text-center text-sm text-[#6B8E7F] mt-4';
                        form.reset();
                      } else {
                        statusEl.textContent = '送信に失敗しました。お手数ですが、もう一度お試しください。';
                        statusEl.className = 'text-center text-sm text-red-500 mt-4';
                      }
                    }
                  })
                  .catch(() => {
                    if (statusEl) {
                      statusEl.textContent = '送信に失敗しました。お手数ですが、もう一度お試しください。';
                      statusEl.className = 'text-center text-sm text-red-500 mt-4';
                    }
                  });
              }}
            >
              <div className="space-y-4">
                {/* Menu Selection */}
                <div>
                  <label className="block text-sm font-light text-[#4A4A4A] mb-1.5">
                    鑑定メニュー <span className="text-[#D4A574]">*</span>
                  </label>
                  <select
                    name="menu"
                    required
                    className="w-full px-4 py-2.5 border border-[#E0E0E0] rounded-lg text-sm focus:outline-none focus:border-[#6B8E7F] transition-colors bg-white"
                  >
                    <option value="">鑑定メニューを選択してください</option>
                    <option value="初回鑑定（オンライン30分）｜4,000円">初回鑑定（オンライン30分）｜4,000円</option>
                    <option value="オンライン鑑定（30分）｜5,000円">オンライン鑑定（30分）｜5,000円</option>
                    <option value="オンライン鑑定（60分）｜10,000円">オンライン鑑定（60分）｜10,000円</option>
                    <option value="対面鑑定（30分）｜6,000円">対面鑑定（30分）｜6,000円</option>
                    <option value="対面鑑定（60分）｜11,000円">対面鑑定（60分）｜11,000円</option>
                  </select>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-light text-[#4A4A4A] mb-1.5">
                    お名前 <span className="text-[#D4A574]">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 border border-[#E0E0E0] rounded-lg text-sm focus:outline-none focus:border-[#6B8E7F] transition-colors"
                    placeholder="例：山田花子"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-light text-[#4A4A4A] mb-1.5">
                    メールアドレス <span className="text-[#D4A574]">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 border border-[#E0E0E0] rounded-lg text-sm focus:outline-none focus:border-[#6B8E7F] transition-colors"
                    placeholder="example@email.com"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-light text-[#4A4A4A] mb-1.5">
                    携帯番号 <span className="text-[#D4A574]">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-4 py-2.5 border border-[#E0E0E0] rounded-lg text-sm focus:outline-none focus:border-[#6B8E7F] transition-colors"
                    placeholder="例：090-1234-5678"
                  />
                </div>

                {/* Preferred Date/Time */}
                <div>
                  <label className="block text-sm font-light text-[#4A4A4A] mb-1.5">
                    ご希望日時 <span className="text-[#D4A574]">*</span>
                  </label>
                  <input
                    type="text"
                    name="datetime"
                    required
                    className="w-full px-4 py-2.5 border border-[#E0E0E0] rounded-lg text-sm focus:outline-none focus:border-[#6B8E7F] transition-colors"
                    placeholder="例：月曜日 21:00〜、または第1・第3木曜日など"
                  />
                  <p className="text-xs text-[#999999] mt-1">
                    営業時間：月・水・木・金　16:00〜23:00
                  </p>
                </div>

                {/* Consultation Content */}
                <div>
                  <label className="block text-sm font-light text-[#4A4A4A] mb-1.5">
                    ご相談内容 <span className="text-[#D4A574]">*</span>
                  </label>
                  <textarea
                    name="content"
                    rows={4}
                    required
                    maxLength={500}
                    placeholder="ご相談されたい内容をご記入ください（500文字以内）"
                    className="w-full px-4 py-2.5 border border-[#E0E0E0] rounded-lg text-sm focus:outline-none focus:border-[#6B8E7F] transition-colors resize-none"
                  ></textarea>
                  <p className="text-xs text-[#999999] mt-1">500文字以内</p>
                </div>

                {/* Divination Info */}
                <div>
                  <label className="block text-sm font-light text-[#4A4A4A] mb-1.5">
                    鑑定に必要な情報
                  </label>
                  <textarea
                    name="divination-info"
                    rows={5}
                    maxLength={500}
                    placeholder="ご本人の生年月日・性別&#10;相性鑑定の場合：お相手の生年月日・性別&#10;親子鑑定の場合：ご同居家族全員の生年月日・性別"
                    className="w-full px-4 py-2.5 border border-[#E0E0E0] rounded-lg text-sm focus:outline-none focus:border-[#6B8E7F] transition-colors resize-none"
                  ></textarea>
                  <p className="text-xs text-[#999999] mt-1">500文字以内</p>
                </div>

                <button
                  type="submit"
                  className="w-full py-3 bg-[#6B8E7F] text-white rounded-full text-sm font-light tracking-wider hover:bg-[#5A7D6E] transition-colors whitespace-nowrap cursor-pointer"
                >
                  鑑定を申し込む
                </button>
                <p id="form-status" className="text-center text-sm mt-3"></p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------------------- */}
      {/* CTA Section */}
      {/* --------------------------------------------------------------------- */}
      <section className="py-16 sm:py-24 lg:py-32 bg-[#F5F1E8]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <h2
            className="text-3xl sm:text-4xl lg:text-6xl font-bold text-[#2C2C2C] mb-4 sm:mb-6 leading-tight"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            あなたの人生に、<br />
            新しい視点を。
          </h2>
          <p className="text-[#8B7355] text-base sm:text-lg font-light mb-8 sm:mb-12">
            初回特別セッション、受付中です
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => scrollToSection('contact')}
              className="px-10 sm:px-14 py-4 sm:py-5 bg-[#6B8E7F] text-white rounded-full text-sm sm:text-base font-light tracking-wider hover:bg-[#5A7D6E] transition-colors inline-flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer w-full sm:w-auto"
            >
              <span>鑑定を申し込む</span>
              <i className="ri-arrow-right-line text-lg sm:text-xl"></i>
            </button>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------------------- */}
      {/* Footer */}
      {/* --------------------------------------------------------------------- */}
      <footer className="bg-[#8B4513] text-white py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-8 sm:mb-12">
            {/* Branding */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center space-x-2 sm:space-x-3 mb-4">
                <img
                  src="https://public.readdy.ai/ai/img_res/0ef220cc-5e3a-48f0-bc37-23fb3250621c.png"
                  alt="橘穂の算命学カウンセリング ロゴ"
                  className="w-8 h-8 sm:h-10 sm:w-10"
                />
                <span className="text-sm sm:text-base font-serif whitespace-nowrap">
                  橘穂の算命学カウンセリング
                </span>
              </div>
              <p className="text-[10px] sm:text-xs font-light text-white/80 mb-4">
                女性限定・完全予約制
              </p>
              <p className="text-[10px] sm:text-xs font-light text-white/60">
                算命学占星術認定鑑定士
              </p>
            </div>

            {/* Service links */}
            <div>
              <h4 className="text-xs sm:text-sm font-semibold mb-3 sm:mb-4 pb-2 border-b border-white/20">
                サービス
              </h4>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <Link
                    to="/about"
                    className="text-xs sm:text-sm font-light hover:text-white/80 transition-colors whitespace-nowrap"
                  >
                    算命学とは
                  </Link>
                </li>
                <li>
                  <Link
                    to="/profile"
                    className="text-xs sm:text-sm font-light hover:text-white/80 transition-colors whitespace-nowrap"
                  >
                    プロフィール
                  </Link>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('menu')}
                    className="text-xs sm:text-sm font-light hover:text-white/80 transition-colors whitespace-nowrap"
                  >
                    鑑定メニュー
                  </button>
                </li>
              </ul>
            </div>

            {/* Reservation links */}
            <div>
              <h4 className="text-xs sm:text-sm font-semibold mb-3 sm:mb-4 pb-2 border-b border-white/20">
                ご予約
              </h4>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <span className="text-xs sm:text-sm font-light cursor-pointer hover:text-white/80 transition-colors whitespace-nowrap">
                    営業時間
                  </span>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('contact')}
                    className="text-xs sm:text-sm font-light hover:text-white/80 transition-colors whitespace-nowrap"
                  >
                    予約方法
                  </button>
                </li>
              </ul>
            </div>

            {/* Conditions */}
            <div>
              <h4 className="text-xs sm:text-sm font-semibold mb-3 sm:mb-4 pb-2 border-b border-white/20">
                ご利用条件
              </h4>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <span className="text-xs sm:text-sm font-light cursor-pointer hover:text-white/80 transition-colors whitespace-nowrap">
                    女性限定について
                  </span>
                </li>
                <li>
                  <span className="text-xs sm:text-sm font-light cursor-pointer hover:text-white/80 transition-colors whitespace-nowrap">
                    対面鑑定エリア
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/20 pt-6 sm:pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <img
                src="https://public.readdy.ai/ai/img_res/0ef220cc-5e3a-48f0-bc37-23fb3250621c.png"
                alt="橘穂の算命学カウンセリング"
                className="h-7 w-7 sm:h-8 sm:w-8 object-contain"
              />
              <div>
                <div className="text-sm sm:text-lg font-light">
                  橘穂の算命学カウンセリング
                </div>
                <p className="text-[10px] sm:text-xs font-light mt-1">
                  女性限定・完全予約制
                </p>
              </div>
            </div>

            <div className="text-center sm:text-right">
              <p className="text-[10px] sm:text-xs font-light mb-2">
                © 2025 All Rights Reserved
              </p>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] sm:text-xs font-light hover:text-white/80 transition-colors whitespace-nowrap"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
